import { MoneyMapFilterState } from '@fruitful/money-map';
import { useCallback, useEffect, useRef, useState } from 'react';
import { type LayoutChangeEvent } from 'react-native';
import Animated, {
    useAnimatedStyle,
    useSharedValue,
    withTiming,
} from 'react-native-reanimated';
import {
    ArrowRightLeftIcon,
    CreditCardIcon,
    FileTextIcon,
    type IconType,
    SproutIcon,
} from 'smoothie';
import { Icon, LabelRegular, View, XStack, YStack } from 'smoothie';

const createAnimatedComponent = Animated.createAnimatedComponent;
const AnimatedView = createAnimatedComponent(View);

interface MoneyMapVisualExplainerProps {
    filter: MoneyMapFilterState;
}

const FILTER_CONTENT: Record<
    MoneyMapFilterState,
    {
        icon: IconType;
        title: string | undefined;
        text: string;
    }
> = {
    [MoneyMapFilterState.BILLS]: {
        icon: FileTextIcon,
        title: undefined,
        text: 'Set aside enough to pay all of your bills, so the rest can go towards spending and goals.',
    },
    [MoneyMapFilterState.SPEND]: {
        icon: CreditCardIcon,
        title: undefined,
        text: 'Feel empowered in your day-to-day spending on groceries, gas, shopping, eating out, and other fun stuff with clarity into what you have free to spend, so you never dip into money for bills or goals.',
    },
    [MoneyMapFilterState.GOALS]: {
        icon: SproutIcon,
        title: undefined,
        text: 'Automatically make progress on your goals according to their priority by automating your monthly savings, and pushing any extra income towards achieving your goals even sooner.',
    },
    [MoneyMapFilterState.ALL]: {
        icon: ArrowRightLeftIcon,
        title: 'Tap above to explore your Fruitful Money Map',
        text: "With Fruitful, you're setting up a financial system that works for you — automatically. Set aside enough for all your bills, save for your goals effortlessly, and you'll always know exactly what's free to spend. We simplify your finances so you can focus on what matters, while your money works in the background.",
    },
};

const TriangleNotch = ({ left }: { left?: string }) => {
    const leftValue = useSharedValue(0);
    const previousLeft = useRef<string | undefined>(undefined);

    useEffect(() => {
        if (left?.endsWith('%')) {
            const percent = parseFloat(left);

            const shouldAnimate =
                previousLeft.current != null && left !== previousLeft.current;

            leftValue.value = shouldAnimate ? withTiming(percent) : percent;
        }

        previousLeft.current = left;
    }, [left]);

    const animatedStyle = useAnimatedStyle(() => {
        return {
            left: `${leftValue.value}%`,
        };
    });

    return left != null ? (
        <AnimatedView
            style={[
                {
                    position: 'absolute',
                    top: -9,
                    marginLeft: -10,
                    width: 0,
                    height: 0,
                    borderLeftWidth: 10,
                    borderRightWidth: 10,
                    borderBottomWidth: 10,
                    borderLeftColor: 'transparent',
                    borderRightColor: 'transparent',
                    borderBottomColor: '$gray1200',
                    zIndex: 1,
                },
                animatedStyle,
            ]}
        />
    ) : null;
};

export const MoneyMapVisualExplainer = ({
    filter,
}: MoneyMapVisualExplainerProps) => {
    const [isMultiline, setIsMultiline] = useState(false);
    const [containerWidth, setContainerWidth] = useState(0);

    const allFilters = [
        MoneyMapFilterState.BILLS,
        MoneyMapFilterState.SPEND,
        MoneyMapFilterState.GOALS,
    ];
    const activeIndex = allFilters.findIndex((f) => f === filter);

    const getNotchPercent = (index: number, width: number): string => {
        const visualMaxWidth = 402;
        const visualWidth = Math.min(width, visualMaxWidth);
        const padding = 24;
        const gap = 16;
        const count = 3;
        const barWidth =
            (visualWidth - padding * 2 - gap * (count - 1)) / count;
        const barCenterInVisual =
            padding + index * (barWidth + gap) + barWidth / 2;
        const visualOffset = (width - visualWidth) / 2;
        const barCenterInContainer = visualOffset + barCenterInVisual;
        return `${(barCenterInContainer / width) * 100}%`;
    };

    const notchX =
        activeIndex >= 0 && containerWidth > 0
            ? getNotchPercent(activeIndex, containerWidth)
            : undefined;

    const { icon, title, text } = FILTER_CONTENT[filter] ?? FILTER_CONTENT.ALL;

    const handleLayout = useCallback((e: LayoutChangeEvent) => {
        const { height } = e.nativeEvent.layout;
        setIsMultiline(height > 18); // Single-line height is roughly ~14, add a buffer to be safe
    }, []);

    return (
        <YStack
            position="relative"
            alignItems="center"
            onLayout={(e: LayoutChangeEvent) => {
                setContainerWidth(e.nativeEvent.layout.width);
            }}
        >
            <TriangleNotch left={notchX} />
            <XStack
                backgroundColor="$accent1000"
                padding={16}
                borderRadius={8}
                gap={8}
                width="100%"
                alignItems={isMultiline ? 'flex-start' : 'center'}
            >
                <View>
                    <Icon icon={icon} color="$white" size={20} />
                </View>
                <YStack gap={8} flexShrink={1}>
                    {title != null && (
                        <LabelRegular color="$dropletBlue500">
                            {title}
                        </LabelRegular>
                    )}
                    {/* @ts-expect-error - onLayout is valid. */}
                    <LabelRegular color="$white" onLayout={handleLayout}>
                        {text}
                    </LabelRegular>
                </YStack>
            </XStack>
        </YStack>
    );
};
